import i18next, { TFunction } from "i18next";
import { initData } from "@telegram-apps/sdk";
import resourcesToBackend from "i18next-resources-to-backend";
import LanguageDetector from "i18next-browser-languagedetector";
import {
  fallbackLanguage,
  isSupportedLanguage,
} from "../../configs/localization";

export const LOCALE_CACHE_KEY = "user-language";

/**
 * Get user's language code from Telegram launch parameters
 * If not available or not supported, falls back to 'en'
 */
export function getTelegramLanguage(): string {
  console.log("🔍 getTelegramLanguage() called");

  try {
    initData.restore();
    // Access user data safely
    const user = initData.user();

    if (user?.language_code) {
      const userLang = user.language_code;
      console.log("🌐 User language from Telegram:", userLang);

      // If the exact language is supported, use it
      if (isSupportedLanguage(userLang)) {
        console.log("✅ Exact language supported:", userLang);
        return userLang;
      }

      // If the language has a base code (e.g. zh-CN -> zh), try to match it
      const baseLang = (userLang as string).split("-")[0];
      if (isSupportedLanguage(baseLang)) {
        console.log("✅ Base language supported:", baseLang);
        return baseLang;
      }

      console.log("❌ Language not supported:", userLang);
    } else {
      console.log("❌ No language_code found in user data");
    }
  } catch (error) {
    console.warn("Failed to get user language:", error);
  }

  console.log("🔄 Falling back to:", fallbackLanguage);
  return fallbackLanguage;
}

// Export for backward compatibility with proper storage priority
export function getUserLanguage(): string {
  // First check localStorage for saved preference
  const savedLanguage = localStorage.getItem(LOCALE_CACHE_KEY);
  if (savedLanguage && isSupportedLanguage(savedLanguage)) {
    return savedLanguage;
  } else {
    console.error("Not supported language in localStorage:", savedLanguage);
  }

  // Fall back to Telegram language if no saved preference
  return getTelegramLanguage();
}

export const initLanguage = async () => {
  // Check if we have a saved language BEFORE initializing i18next
  const savedLanguage = localStorage.getItem(LOCALE_CACHE_KEY);
  console.log(
    "💾 Saved language from localStorage (before init):",
    savedLanguage
  );

  let initialLanguage: string;

  if (!savedLanguage || !isSupportedLanguage(savedLanguage)) {
    // First time user - get Telegram system language
    initialLanguage = getTelegramLanguage();
    console.log(
      "🆕 First time user, will use Telegram language:",
      initialLanguage
    );
    // Save it immediately to prevent LanguageDetector from overriding
    localStorage.setItem(LOCALE_CACHE_KEY, initialLanguage);
  } else {
    // Returning user - use saved preference
    initialLanguage = savedLanguage;
    console.log("🔄 Returning user, will use saved language:", initialLanguage);
  }

  // Initialize i18next with dynamic loading via HTTP
  return new Promise<void>((resolve, reject) => {
    i18next
      .use(
        resourcesToBackend((language: string, namespace: string) => {
          console.log(`📦 Loading locale: ${language}/${namespace}`);
          return fetch(`/locales/${language}.json`).then((response) => {
            if (!response.ok) {
              throw new Error(
                `Failed to load locale ${language}: ${response.status}`
              );
            }
            return response.json();
          });
        })
      )
      .use(LanguageDetector)
      .init(
        {
          lng: initialLanguage,
          fallbackLng: fallbackLanguage,
          ns: ["translation"],
          defaultNS: "translation",
          detection: {
            order: ["localStorage"], // Only check localStorage, no browser detection
            lookupLocalStorage: LOCALE_CACHE_KEY,
            caches: ["localStorage"],
          },
          interpolation: {
            escapeValue: false,
          },
          // Important: Don't preload resources
          preload: false,
          // Load resources on demand
          partialBundledLanguages: true,
          // Disable sync loading
          load: "languageOnly",
        },
        (err) => {
          if (err) {
            console.error("Failed to initialize i18next:", err);
            reject(err);
          } else {
            console.log(
              `🎯 i18next initialized with dynamic loading for language: ${initialLanguage}`
            );

            // Wait for the initial language resources to be loaded
            const checkResourcesLoaded = () => {
              if (i18next.hasResourceBundle(initialLanguage, "translation")) {
                console.log(
                  `✅ Locale resources loaded for: ${initialLanguage}`
                );
                resolve();
              } else {
                // If resources aren't loaded yet, wait a bit and check again
                setTimeout(checkResourcesLoaded, 50);
              }
            };

            checkResourcesLoaded();
          }
        }
      );
  });
};

/**
 * Translate a key
 * @param key Translation key
 * @param options Translation options
 * @returns Translated string
 */
export function t(key: string, options?: Record<string, any>): string {
  return i18next.t(key, options);
}

/**
 * Change language with dynamic loading
 * @param lang Language code
 * @returns Promise that resolves when language is changed
 */
export async function changeLanguage(lang: string): Promise<TFunction> {
  console.log("🔄 Changing language to:", lang);

  if (!isSupportedLanguage(lang)) {
    const fallback = getTelegramLanguage();
    console.warn(
      `Language ${lang} is not supported. Using fallback: ${fallback}`
    );
    lang = fallback;
  }

  // Save the language preference
  localStorage.setItem(LOCALE_CACHE_KEY, lang);

  // i18next-resources-to-backend will automatically load the language
  return i18next.changeLanguage(lang);
}

// Export the i18next instance for advanced usage
export default i18next;
